/* eslint-disable prettier/prettier */
/* eslint-disable no-console */
// utils/calculateVentas.js

/**
 * ============================================================================
 * UTILIDAD CONSOLIDADA PARA CÁLCULO DE VENTAS
 * ============================================================================
 *
 * Calcula cierres y facturación para todas las verticales:
 * - Publicidad (PAUTA)
 * - Hardware (CAPEX/OPEX)
 * - Gestión & Contenidos (Cartelería)
 * - BTL
 *
 * Tipos de cambio delegados a exchangeRateService.js (fuente centralizada)
 *
 * USO: ER, VentasTabla, VentasDetalle, VentasProyeccion, etc.
 */

import {
  loadExchangeRates,
  getUFValue as _getUFValue,
  getDolarValue as _getDolarValue,
  convertirAMonedaLocal as _convertirAMonedaLocal,
} from "./exchangeRateService";

// Re-exportar para compatibilidad con importadores existentes
export const getUFValue = _getUFValue;
export const getDolarValue = _getDolarValue;
export const convertirAMonedaLocal = _convertirAMonedaLocal;

// ============================================================================
// 2. FUNCIONES LEGACY (mantener compatibilidad)
// ============================================================================

export const getConvertedImporte = (oportunidad, rate) =>
  oportunidad.moneda_dolar ? (oportunidad.importe || 0) * rate : oportunidad.importe || 0;

export const getConvertedImporteBTL = (oportunidadBTL, rate) =>
  oportunidadBTL.moneda_dolar ? (oportunidadBTL.total_investment || 0) * rate : oportunidadBTL.total_investment || 0;

/**
 * Determina la tasa de cambio correcta para una oportunidad/contrato.
 * Prioridad: facturafecha → fecha_cierre → mes_cierre → fallback.
 * @param {object} opp - Oportunidad con moneda_dolar, facturafecha, fecha_cierre, mes_cierre
 * @param {function} getDolarValueFn - Función (fechaStr) => number (ya bound al país)
 * @param {number} [fallback=1] - Valor fallback si no hay fecha disponible
 */
export const getRateForOpp = (opp, getDolarValueFn, fallback = 1) => {
  if (!opp.moneda_dolar) return 1;
  if (opp.facturafecha) return getDolarValueFn(opp.facturafecha);
  if (opp.fecha_cierre) return getDolarValueFn(opp.fecha_cierre);
  if (opp.mes_cierre) {
    const [mes, ano] = opp.mes_cierre.split("-");
    if (mes && ano) return getDolarValueFn(`${ano}-${mes}-15`);
  }
  return fallback;
};

/**
 * Convierte el importe de una oportunidad a USD usando rate histórico por fecha.
 * Si la oportunidad ya está en USD (moneda_dolar=true), retorna el importe sin tocar.
 * Caso contrario, divide por el dólar de la fecha relevante.
 * Misma cascada de fechas que getRateForOpp: facturafecha → fecha_cierre → mes_cierre → fallback.
 * @param {object} opp - Oportunidad con moneda_dolar, importe/total_investment, fechas
 * @param {function} getDolarValueFn - Función (fechaStr) => number (ya bound al país)
 * @param {number} [fallback=1] - Rate fallback si no hay fecha disponible
 */
export const getImporteEnUSD = (opp, getDolarValueFn, fallback = 1) => {
  const importe = Number(opp.importe || opp.total_investment || 0);
  if (opp.moneda_dolar) return importe;
  let rate = fallback;
  if (opp.facturafecha) rate = getDolarValueFn(opp.facturafecha) || fallback;
  else if (opp.fecha_cierre) rate = getDolarValueFn(opp.fecha_cierre) || fallback;
  else if (opp.mes_cierre) {
    const [mes, ano] = opp.mes_cierre.split("-");
    if (mes && ano) rate = getDolarValueFn(`${ano}-${mes}-15`) || fallback;
  }
  return rate > 0 ? importe / rate : 0;
};

export const normalizeMonth = (month, createdAt, ano = null) => {
  if (!month || typeof month !== "string") {
    if (ano) return `01-${ano}`;
    return null;
  }
  if (/^\d{2}-\d{4}$/.test(month)) return month;
  const monthNum = parseInt(month, 10);
  if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12 && ano) {
    return `${monthNum.toString().padStart(2, "0")}-${ano}`;
  }
  return null;
};

export const calculateSalesSums = (
  oportunidades,
  oportunidadesBTL,
  year,
  startMonth = 1,
  endMonth = 12,
  rate,
  pais
) => {
  const filterAndSum = (ops, status, field, includeMin = false, isBTL = false) => {
    const filteredOps = ops.filter((op) => {
      let effectiveMonth, effectiveYear;

      // Para facturación: prioridad 1 = facturafecha, prioridad 2 = mes_factura
      if (field === "mes_factura") {
        if (op.facturafecha) {
          const facturaDate = new Date(op.facturafecha);
          if (!isNaN(facturaDate.getTime())) {
            effectiveMonth = facturaDate.getMonth() + 1;
            effectiveYear = facturaDate.getFullYear();
          }
        }
        if (!effectiveMonth && op.mes_factura) {
          const [mesStr, anoStr] = op.mes_factura.split("-").map(Number);
          effectiveMonth = mesStr;
          effectiveYear = anoStr;
        }
      } else {
        // Para cierres y otros: usar el campo directamente
        if (!op[field]) return false;
        const [mesStr, anoStr] = op[field].split("-").map(Number);
        effectiveMonth = mesStr;
        effectiveYear = anoStr;
      }

      // Si no hay fecha válida, descartar
      if (!effectiveMonth || !effectiveYear) return false;

      const estatus = isBTL ? (op.status === 100 ? "100%" : `${op.status || 0}%`) : op.estatus;
      return (
        estatus === status &&
        !op.eliminada &&
        effectiveYear === parseInt(year) &&
        effectiveMonth >= startMonth &&
        effectiveMonth <= endMonth &&
        op.pais === pais
      );
    });
    const groups = {};
    filteredOps.forEach((op) => {
      const key = op.client_id;
      if (!groups[key]) groups[key] = [];
      const importe = isBTL ? getConvertedImporteBTL(op, rate) : getConvertedImporte(op, rate);
      groups[key].push(importe);
    });
    let total = 0;
    for (const key in groups) {
      const importes = groups[key];
      total += includeMin && importes.length > 1 ? Math.min(...importes) : importes.reduce((sum, val) => sum + val, 0);
    }
    return total;
  };

  const sumCierresRegular = filterAndSum(oportunidades, "100%", "mes_cierre", false, false);
  const sumCierresBTL = filterAndSum(oportunidadesBTL, "100%", "mes_cierre", false, true);
  const sumaImportes100Regular = filterAndSum(oportunidades, "100%", "mes_factura", false, false);
  const sumaImportes100BTL = filterAndSum(oportunidadesBTL, "100%", "mes_factura", false, true);
  const sumaImportes90 = filterAndSum(oportunidades, "90%", "mes_cierre", true, false);
  const sumaImportes75 = filterAndSum(oportunidades, "75%", "mes_cierre", true, false);
  const sumaImportes50 = filterAndSum(oportunidades, "50%", "mes_cierre", true, false);
  const sumaImportes0 = filterAndSum(oportunidades, "0%", "mes_cierre", true, false);

  return {
    sumCierres: sumCierresRegular + sumCierresBTL,
    sumaImportes100: sumaImportes100Regular + sumaImportes100BTL,
    sumaImportes90,
    sumaImportes75,
    sumaImportes50,
    sumaImportes0,
  };
};

export const calculateForecast = (sum90, sum75) => sum90 * 0.75 + sum75 * 0.5;

// ============================================================================
// 3. NUEVAS FUNCIONES: CÁLCULOS MENSUALES CONSOLIDADOS
// ============================================================================

/**
 * Calcula ingresos mensuales de PUBLICIDAD (facturación)
 * LÓGICA IDÉNTICA A ER: solo incluye oportunidades con facturafecha
 * @param {number} ano - Año a calcular
 * @param {Array} oportunidades - Array de oportunidades
 * @param {string} pais - "Chile" o "Perú"
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
export const calcularPublicidadMensual = async (ano, oportunidades, pais) => {
  await loadExchangeRates();

  const valores = Array(12).fill(0);

  oportunidades
    // FILTRO IGUAL QUE ER: solo con facturafecha (facturado real)
    .filter((opp) => !opp.eliminada && opp.facturafecha)
    .forEach((opp) => {
      const facturaDate = new Date(opp.facturafecha);
      if (isNaN(facturaDate.getTime())) return;

      const monthIndex = facturaDate.getMonth();
      const facturaYear = facturaDate.getFullYear();

      if (facturaYear === ano && monthIndex >= 0 && monthIndex < 12) {
        const importe = parseFloat(opp.importe) || 0;
        // Conversión USD → moneda local por FECHA de factura (dólar del día),
        // no por tasa única. Respeta la lógica histórica del proyecto.
        const valorLocal = opp.moneda_dolar
          ? _convertirAMonedaLocal(importe, "USD", pais, opp.facturafecha)
          : importe;
        valores[monthIndex] += valorLocal;
      }
    });

  return valores;
};

/**
 * Calcula ingresos mensuales de BTL (facturación)
 * LÓGICA IDÉNTICA A ER: solo incluye registros con facturafecha
 * @param {number} ano - Año a calcular
 * @param {Array} salesBTL - Array de ventas BTL
 * @param {string} pais - "Chile" o "Perú" (no usado, para compatibilidad)
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
// eslint-disable-next-line no-unused-vars
export const calcularBTLMensual = async (ano, salesBTL, pais) => {
  const mapa = Array(12).fill(0);

  salesBTL.forEach((row) => {
    // FILTRO IGUAL QUE ER: solo con facturafecha
    if (!row.facturafecha) return;

    const fecha = new Date(row.facturafecha);
    if (isNaN(fecha.getTime())) return;
    if (fecha.getFullYear() !== ano) return;

    const mes = fecha.getMonth();
    // ER usa directo total_investment sin conversión
    mapa[mes] += Number(row.total_investment) || 0;
  });

  return mapa;
};

/**
 * Calcula ingresos mensuales de HARDWARE (facturación)
 * Extraído del ER - lógica exacta con CAPEX/OPEX
 * @param {number} ano - Año a calcular
 * @param {Array} negociosCarteleria - Array de negocios cartelería
 * @param {string} pais - "Chile" o "Perú"
 * @param {Array} costosGestionData - Datos de costos gestión (para fechas factura OPEX)
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
export const calcularHardwareMensual = async (ano, negociosCarteleria, pais, costosGestionData = []) => {
  await loadExchangeRates();

  const mapa = Array(12).fill(0);

  negociosCarteleria.forEach((n) => {
    if (!n.incluye_hardware || n.eliminada) return;
    if (!["100%", "Activo"].includes(n.estado)) return;

    const moneda = (n.moneda_hardware || "UF").toUpperCase();
    let yaProcesadoConDesglose = false;

    // === CAPEX CON DESGLOSE DE PAGOS ===
    if (
      n.modalidad === "CAPEX" &&
      n.detalle_pagos_hardware &&
      Array.isArray(n.detalle_pagos_hardware) &&
      n.detalle_pagos_hardware.some((p) => p.fecha_factura && p.monto != null)
    ) {
      n.detalle_pagos_hardware.forEach((pago) => {
        if (!pago.fecha_factura || pago.monto == null) return;

        const fecha = new Date(pago.fecha_factura);
        const mes = fecha.getMonth();
        const año = fecha.getFullYear();

        if (año !== ano) return;

        const monto = Number(pago.monto);
        const fechaUsada = pago.fecha_factura.split("T")[0];

        mapa[mes] += convertirAMonedaLocal(monto, moneda, pais, fechaUsada);
      });
      yaProcesadoConDesglose = true;
    }

    // === OTROS CAPEX (instalación, etc.) ===
    if (
      n.modalidad === "CAPEX" &&
      n.detalle_otros_hardware &&
      Array.isArray(n.detalle_otros_hardware) &&
      n.detalle_otros_hardware.length > 0
    ) {
      n.detalle_otros_hardware.forEach((pago) => {
        if (!pago.fecha_factura || pago.monto == null) return;

        const fecha = new Date(pago.fecha_factura);
        const mes = fecha.getMonth();
        const año = fecha.getFullYear();

        if (año !== ano) return;

        const monto = Number(pago.monto);
        const fechaUsada = pago.fecha_factura.split("T")[0];

        mapa[mes] += convertirAMonedaLocal(monto, moneda, pais, fechaUsada);
      });
    }

    // === CAPEX SIN DESGLOSE ===
    if (n.modalidad === "CAPEX" && n.fecha_cierre && !yaProcesadoConDesglose) {
      const fechaCierre = new Date(n.fecha_cierre);
      const mesCierre = fechaCierre.getMonth();
      const añoCierre = fechaCierre.getFullYear();

      if (añoCierre !== ano) return;

      const monto = Number(n.monto_hardware) || 0;
      const ultimoDia = new Date(ano, mesCierre + 1, 0);
      const fechaStr = ultimoDia.toISOString().split("T")[0];

      mapa[mesCierre] += convertirAMonedaLocal(monto, moneda, pais, fechaStr);
    }

    // === OPEX (cuotas mensuales) ===
    else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
      const fechaCierre = new Date(n.fecha_cierre);
      const mesCierre = fechaCierre.getMonth();
      const añoCierre = fechaCierre.getFullYear();

      const pantallas = Number(n.cantidad_pantallas) || 1;
      const cuotaBase = Number(n.cuota_hardware_opex);
      const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;

      let cuotaActual = 0;
      let añoActual = añoCierre;
      let mesActual = mesCierre;

      while (cuotaActual < totalCuotas) {
        if (añoActual === ano && mesActual >= 0 && mesActual < 12) {
          // Buscar fecha_factura en costosGestionData
          const registro = costosGestionData.find(
            (c) =>
              Number(c.negocio_id) === n.id &&
              Number(c.mes) === mesActual + 1 &&
              Number(c.ano) === ano &&
              c.fecha_factura
          );

          const ultimoDia = new Date(ano, mesActual + 1, 0);
          const fechaFactura = registro?.fecha_factura?.split("T")[0] || ultimoDia.toISOString().split("T")[0];

          const cuotaLocal = cuotaBase * pantallas;
          mapa[mesActual] += convertirAMonedaLocal(cuotaLocal, moneda, pais, fechaFactura);
        }

        cuotaActual++;
        mesActual++;
        if (mesActual >= 12) {
          mesActual = 0;
          añoActual++;
        }
        if (añoActual > ano + 1) break;
      }
    }
  });

  return mapa;
};

/**
 * Calcula ingresos mensuales de GESTIÓN Y CONTENIDOS (facturación)
 * Extraído del ER - lógica exacta
 * @param {number} ano - Año a calcular
 * @param {Array} negociosCarteleria - Array de negocios cartelería
 * @param {string} pais - "Chile" o "Perú"
 * @param {Array} costosGestionData - Datos de costos gestión (para fechas factura)
 * @returns {Array<number>} Array(12) con ingresos por mes
 */
export const calcularGestionContenidosMensual = async (ano, negociosCarteleria, pais, costosGestionData = []) => {
  await loadExchangeRates();

  const mapa = Array(12).fill(0);

  negociosCarteleria.forEach((negocio) => {
    if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;

    const fechaInicio = new Date(negocio.fecha_inicio);
    const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
    if (plazoMeses === 0) return;

    const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
    const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
    const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;

    const getFechaFactura = (mesIndex) => {
      const registro = costosGestionData.find(
        (c) =>
          Number(c.negocio_id) === negocio.id &&
          Number(c.mes) === mesIndex + 1 &&
          Number(c.ano) === ano &&
          c.fecha_factura
      );
      return registro?.fecha_factura?.split("T")[0] || null;
    };

    // Calcular fecha fin del contrato
    const fechaFin = new Date(fechaInicio);
    fechaFin.setMonth(fechaFin.getMonth() + plazoMeses);

    for (let m = 0; m < 12; m++) {
      const ultimoDiaMes = new Date(ano, m + 1, 0);
      const primerDiaMes = new Date(ano, m, 1);

      // Contrato activo en este mes: ya empezó Y aún no terminó
      if (fechaInicio <= ultimoDiaMes && primerDiaMes < fechaFin) {
        const fechaFactura = getFechaFactura(m);
        const fechaParaUF = fechaFactura || ultimoDiaMes.toISOString().split("T")[0];

        let ingresoLocal = 0;

        if (gestionUnitario > 0) {
          ingresoLocal += convertirAMonedaLocal(gestionUnitario * pantallas, negocio.moneda_gestion, pais, fechaParaUF);
        }

        if (contenidosUnitario > 0) {
          ingresoLocal += convertirAMonedaLocal(
            contenidosUnitario * pantallas,
            negocio.moneda_contenidos,
            pais,
            fechaParaUF
          );
        }

        mapa[m] += ingresoLocal;
      }
    }
  });

  return mapa;
};

/**
 * Calcula facturación total mensual (suma de todas las verticales)
 * @param {number} ano - Año a calcular
 * @param {Array} oportunidades - Oportunidades publicidad
 * @param {Array} salesBTL - Ventas BTL
 * @param {Array} negociosCarteleria - Negocios cartelería
 * @param {Array} costosGestionData - Datos costos gestión
 * @param {string} pais - "Chile" o "Perú"
 * @returns {Array<number>} Array(12) con facturación total por mes
 */
export const calcularFacturacionTotal = async (
  ano,
  oportunidades,
  salesBTL,
  negociosCarteleria,
  costosGestionData,
  pais
) => {
  const [publicidad, btl, hardware, gestion] = await Promise.all([
    calcularPublicidadMensual(ano, oportunidades, pais),
    calcularBTLMensual(ano, salesBTL, pais),
    calcularHardwareMensual(ano, negociosCarteleria, pais, costosGestionData),
    calcularGestionContenidosMensual(ano, negociosCarteleria, pais, costosGestionData),
  ]);

  return Array(12)
    .fill(0)
    .map((_, i) => (publicidad[i] || 0) + (btl[i] || 0) + (hardware[i] || 0) + (gestion[i] || 0));
};

/**
 * Helper: Calcular YTD (Year To Date)
 * @param {Array<number>} arrayMensual - Array con valores mensuales
 * @param {number} monthsToShow - Cantidad de meses a incluir
 * @returns {number} Suma acumulada
 */
export const calcularYTD = (arrayMensual, monthsToShow) =>
  arrayMensual.slice(0, monthsToShow).reduce((sum, val) => sum + val, 0);

// ============================================================================
// EXPORTACIÓN CONSOLIDADA
// ============================================================================

export default {
  // Conversión
  getUFValue,
  getDolarValue,
  convertirAMonedaLocal,

  // Legacy (compatibilidad)
  getConvertedImporte,
  getConvertedImporteBTL,
  normalizeMonth,
  calculateSalesSums,
  calculateForecast,

  // Cálculos mensuales
  calcularPublicidadMensual,
  calcularBTLMensual,
  calcularHardwareMensual,
  calcularGestionContenidosMensual,
  calcularFacturacionTotal,

  // Helpers
  calcularYTD,
};
